/**
 * The `rooms` repository — Step B of the element-store plan.
 *
 * What it owns today: the per-structure live query, the server re-read, and
 * the WRITE: a local room edit becomes operations by id for its id'd arrays
 * and a merge patch for the rest, applied in one transaction against what
 * the server holds (`roomSave.ts` plans it, the adapter applies it) — one
 * transaction at a time per room, the floor plan's belt removal in the same
 * queue. That is what closes #2716: a local edit to `objects` no longer
 * re-sends the whole array, so an object another client deleted a snapshot
 * ago stays deleted.
 *
 * What stays in `hooks/useRoomsSync` for now: the store itself (the Map, the
 * pending map, the barrier, the echo guard, the snapshot merge). The hook is
 * the rooms SESSION until Step C moves that into a session here beside the
 * structures one; it no longer imports Firebase, which is the assertion's
 * rule for hooks.
 */
import {
  applyRoomMutations,
  readRoomFromServer as readRoomFromServerOnAdapter,
  watchRoomsForStructure as watchOnAdapter,
} from '../firebase/adapters/rooms.adapter';
import type { RoomsSnapshot } from '../firebase/adapters/rooms.adapter';
import { createWriteQueue } from './documentWriteQueue';
import type { WriteQueue } from './documentWriteQueue';
import { isEmptyWrite, planRoomWrite } from './roomSave';
import type {
  RoomBatchResult,
  RoomDoc,
  RoomMutation,
  RoomWrite,
} from '../types/room.types';
import type { ApplyOpsResult } from '../types/structure.types';

export type { RoomsSnapshot };

export const watchRoomsForStructure = watchOnAdapter;
export const readRoomFromServer = readRoomFromServerOnAdapter;

/** What one mutation in a batch resolves with: the batch's skips and arrays, its own removal count. */
interface RoomMutationResult extends ApplyOpsResult {
  written: Record<string, unknown[]>;
  removed: number;
}

/**
 * One transaction at a time per ROOM (`documentWriteQueue.ts`): the store's
 * save and the floor plan's belt removal on the same room no longer contend
 * — what arrives while one is in flight goes as the next batch, composed in
 * order by the adapter. A queue lives as long as the page; a room's is made
 * on its first write.
 */
const queues = new Map<string, WriteQueue<RoomMutation, RoomMutationResult>>();
const queueFor = (roomId: string) => {
  let queue = queues.get(roomId);
  if (!queue) {
    queue = createWriteQueue<RoomMutation, RoomBatchResult, RoomMutationResult>(
      (batch) => applyRoomMutations(roomId, batch),
      (result, index) => ({
        skipped: result.skipped,
        written: result.written,
        removed: result.removed[index] ?? 0,
      })
    );
    queues.set(roomId, queue);
  }
  return queue;
};
export const __resetRoomQueuesForTests = () => queues.clear();

/**
 * Save what one local edit changed on a room. `prev` is the room as the
 * store held it before the edit (the diff basis), `next` as it holds it now,
 * `fields` the changed top-level fields the store's diff found (`null` = no
 * prior version, write the whole body). Nothing to write resolves at once.
 */
export async function saveRoomChanges(
  roomId: string,
  prev: Record<string, unknown> | null | undefined,
  next: Record<string, unknown>,
  fields: string[] | null
): Promise<ApplyOpsResult & { write: RoomWrite }> {
  const write = planRoomWrite(prev, next, fields);
  if (isEmptyWrite(write)) return { skipped: [], write };
  const { skipped, written } = await queueFor(roomId).enqueue({
    kind: 'write',
    write,
  });
  return { skipped, written, write };
}

/** Remove elements of one room array by predicate, against the server's copy. */
export async function removeRoomElementsWhere(
  roomId: string,
  field: string,
  shouldRemove: (element: Record<string, unknown>) => boolean
): Promise<number> {
  const { removed } = await queueFor(roomId).enqueue({
    kind: 'removeWhere',
    field,
    shouldRemove,
  });
  return removed;
}

export type { RoomDoc };
